import { z } from 'zod'

import type { RequestService } from '../request'
import { managedDocSchema } from '@scalar-org/entities/managed-doc'

/**
 * API for interacting with managed OpenAPI documents
 *
 * This is a wrapper around the core/v1/documents endpoints in the core service
 */
export function managedDocsApiFactory(requestService: RequestService) {
  /** Retrieve a managed OpenAPI document from registry */
  async function get(namespace: string, slug: string, semver: string) {
    return await requestService.request({
      url: `core/managed-doc/${namespace}/${slug}/version/${semver}`,
      method: 'get',
      schema: z.string(),
    })
  }

  /** Get the list of all api documents for the namespace */
  async function getApiDocs(namespace: string) {
    return await requestService.request({
      url: `core/managed-doc/${namespace}`,
      method: 'get',
      schema: managedDocSchema
        .pick({
          uid: true,
          version: true,
          title: true,
          slug: true,
          description: true,
          namespace: true,
          isPrivate: true,
          tags: true,
          versions: true,
        })
        .array(),
    })
  }

  /** Upload a new managed OpenAPI document */
  async function create(
    namespace: string,
    data: {
      title: string
      description?: string
      version: string
      document: string
      slug: string
      ruleset?: string
      isPrivate?: boolean
    },
  ) {
    return await requestService.request({
      url: `core/managed-doc/${namespace}`,
      method: 'post',
      data,
      schema: z.object({
        uid: z.string(),
        versionUid: z.string(),
      }),
    })
  }

  /** Update an existing managed OpenAPI document */
  async function update(
    namespace: string,
    slug: string,
    data: Partial<{
      namespace: string
      slug: string
      title: string
      description: string
      isPrivate: boolean
      ruleset: string
      theme: string
    }>,
  ) {
    return await requestService.request({
      url: `core/managed-doc/${namespace}/${slug}`,
      method: 'PATCH',
      data: {
        namespace: data.namespace,
        slug: data.slug,
        title: data.title,
        description: data.description,
        isPrivate: data.isPrivate,
        ruleset: data.ruleset,
        theme: data.theme,
      },
      schema: z.null(),
    })
  }

  /** Add a new version to an existing managed OpenAPI document */
  async function addVersion(
    namespace: string,
    slug: string,
    data: {
      version: string
      document: string
    },
  ) {
    return await requestService.request({
      url: `core/managed-doc/${namespace}/${slug}/version`,
      method: 'post',
      data,
      schema: z.object({ uid: z.string() }),
    })
  }

  /** Update an existing version's managed OpenAPI document */
  async function updateVersion(
    namespace: string,
    slug: string,
    semver: string,
    data: {
      document: string
    },
  ) {
    return await requestService.request({
      url: `core/managed-doc/${namespace}/${slug}/version/${semver}`,
      method: 'PATCH',
      data,
      schema: z.null(),
    })
  }

  /** Delete an existing version's managed OpenAPI document */
  async function deleteVersion(
    namespace: string,
    slug: string,
    semver: string,
  ) {
    return await requestService.request({
      url: `core/managed-doc/${namespace}/${slug}/version/${semver}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  /** Add an access group to the managed OpenAPI document */
  async function addAccessGroup(data: {
    namespace: string
    slug: string
    groupUid: string
  }) {
    return await requestService.request({
      url: 'core/managed-doc/access-group',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  /** Remove an access group to the managed OpenAPI document */
  async function removeAccessGroup(data: {
    namespace: string
    slug: string
    groupUid: string
  }) {
    return await requestService.request({
      url: 'core/managed-doc/access-group',
      method: 'delete',
      data,
      schema: z.null(),
    })
  }

  async function addSdk(data: {
    uid: string
    language: string
  }) {
    return await requestService.request({
      url: 'core/v1/documents/sdk',
      method: 'post',
      data,
      schema: z.null(),
    })
  }
  3
  async function removeSdk(data: {
    uid: string
    language: string
  }) {
    return await requestService.request({
      url: 'core/v1/documents/sdk',
      method: 'delete',
      data,
      schema: z.null(),
    })
  }

  /** Delete an existing managed OpenAPI document */
  async function deleteDoc(namespace: string, slug: string) {
    return await requestService.request({
      url: `core/managed-doc/${namespace}/${slug}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  /** Check if the slug of managed OpenAPI document is available */
  async function isRegistrySlugAvailable(data: {
    namespace: string
    slug: string
  }) {
    return await requestService.request({
      url: 'core/managed-doc/available',
      method: 'post',
      data,
      schema: z.object({
        available: z.boolean(),
      }),
    })
  }

  return {
    getApiDocs,
    get,
    create,
    update,
    delete: deleteDoc,

    addVersion,
    addSdk,
    removeSdk,
    updateVersion,
    deleteVersion,

    addAccessGroup,
    removeAccessGroup,

    isRegistrySlugAvailable,
  }
}
